import styled from '@emotion/styled';
import { Tabs as AntdTab } from 'antd';
import React from 'react';
import { useTranslate } from '@tolgee/react';
import { SearchStoreListParams, StoreInfo } from 'services/api/store';
import { StoreSelectorValue } from './interface';
import QuickSelectButton from './QuickSelectButton';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export type LeftTabKey = 'all' | 'follower' | General.UpperCaseRegion;

interface Props {
  regionList: General.UpperCaseRegion[];
  onChange: React.Dispatch<
    React.SetStateAction<Omit<SearchStoreListParams, 'cursor' | 'page_size'>>
  >;
  followList: StoreInfo[];
  onSubmit?: (values: StoreSelectorValue) => void;
  activeTab: LeftTabKey;
  setActiveTab: (key: LeftTabKey) => void;
  platform?: General.PlatformUpperCaseTunnel;
  quickSelectDisable?: boolean;
}

const { TabPane } = AntdTab;
const Tabs = styled(AntdTab)`
  height: 400px;
  .ant-tabs-nav-list .ant-tabs-tab {
    padding-left: 16px;
    padding-right: 16px;
    height: 40px;
    font-size: 12px;
    margin: 0 !important;
    box-shadow: inset 0 -1px 0 0 rgba(240, 242, 245, 1);
    .ant-tabs-tab-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
  .ant-tabs-nav {
    width: 100%;
  }
  .ant-tabs-content-holder {
    display: none;
  }
`;
const TabContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  .region-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
  .anticon {
    margin-right: 0;
    width: 14px;
    height: 14px;
    display: none;
    svg {
      width: 20px;
      height: 20px;
      color: #84868c;
    }
  }

  &:hover {
    .anticon {
      display: block;
    }
  }
`;
const LeftTab = (props: Props) => {
  const {
    regionList,
    onChange,
    followList,
    onSubmit,
    activeTab,
    setActiveTab,
    platform,
    quickSelectDisable = false,
  } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  return (
    <Tabs
      tabPosition="left"
      activeKey={activeTab}
      onChange={(key) => {
        const nextActive = key as unknown as LeftTabKey;

        switch (nextActive) {
          case 'follower': {
            setActiveTab('follower');
            onChange((pre) => ({ ...pre, platform, region: undefined, follower: '1' }));
            break;
          }
          case 'all': {
            setActiveTab('all');
            onChange((pre) => ({ ...pre, platform, region: undefined, follower: undefined }));

            break;
          }
          default: {
            setActiveTab(nextActive);
            onChange((pre) => ({ ...pre, platform, region: nextActive, follower: undefined }));
          }
        }
      }}
    >
      {followList?.length > 0 ? (
        <TabPane
          tab={
            <TabContent>
              <span className="region-name">{t('storeSelector.Followed')}</span>
              {!quickSelectDisable ? (
                <QuickSelectButton
                  onClick={() =>
                    onSubmit?.({
                      follower: '1',
                      platform,
                    })
                  }
                />
              ) : null}
            </TabContent>
          }
          key="follower"
        />
      ) : null}

      <TabPane
        tab={
          <TabContent>
            <span className="region-name">
              {platform === 'WA_BIZ'
                ? t('storeSelector.AllStoreWA')
                : t('scene.chatWorkspace.conversationList.storeSelector.storeTab.all')}
            </span>
            {!quickSelectDisable ? (
              <QuickSelectButton
                onClick={() =>
                  onSubmit?.({
                    region: undefined,
                    platform,
                  })
                }
              />
            ) : null}
          </TabContent>
        }
        key="all"
      />

      {platform === 'WA_BIZ' || platform === 'FACEBOOK'
        ? null
        : regionList.map((x) => (
            <TabPane
              tab={
                <TabContent>
                  <span className="region-name">{getRegionLabel(x)}</span>
                  {!quickSelectDisable ? (
                    <QuickSelectButton
                      onClick={() =>
                        onSubmit?.({
                          region: x,
                          platform,
                        })
                      }
                    />
                  ) : null}
                </TabContent>
              }
              key={x}
            />
          ))}
    </Tabs>
  );
};

export default LeftTab;
